<template>
  <div>
    <b-row>
      <!-- Settlement Form -->
      <b-col md="6">
        <b-card title="Settlement Details">
          <b-form-group label="Birds Sold">
            <b-form-input v-model="form.birds_sold" type="number" min="0" @input="calculate" />
          </b-form-group>
          <b-form-group label="Average Weight (kg)">
            <b-form-input v-model="form.avg_weight" type="number" step="0.001" min="0" @input="calculate" />
          </b-form-group>
          <b-form-group label="Price per kg (₹)">
            <b-form-input v-model="form.price_per_kg" type="number" step="0.01" min="0" @input="calculate" />
          </b-form-group>
          <b-form-group label="Deductions (₹)">
            <b-form-input v-model="form.deductions" type="number" step="0.01" min="0" @input="calculate" />
          </b-form-group>
          <b-form-group label="Bonus / Penalty (₹)">
            <b-form-input v-model="form.bonus_penalty" type="number" step="0.01" @input="calculate" />
            <small class="text-muted">Positive = bonus, Negative = penalty</small>
          </b-form-group>
          <b-button variant="primary" :disabled="saving" @click="confirmSettlement">
            <b-spinner v-if="saving" small class="mr-1" />
            Confirm Settlement
          </b-button>
        </b-card>
      </b-col>

      <!-- Calculated Summary -->
      <b-col md="6">
        <b-card title="Calculated Summary">
          <div class="settlement-row">
            <span>Gross Amount</span>
            <strong>₹{{ formatNumber(calculated.gross) }}</strong>
          </div>
          <div class="settlement-row">
            <span>Deductions</span>
            <strong class="text-danger">- ₹{{ formatNumber(calculated.deductions) }}</strong>
          </div>
          <div class="settlement-row">
            <span>Bonus / Penalty</span>
            <strong :class="calculated.bonus_penalty >= 0 ? 'text-success' : 'text-danger'">
              {{ calculated.bonus_penalty >= 0 ? '+' : '' }}₹{{ formatNumber(calculated.bonus_penalty) }}
            </strong>
          </div>
          <hr />
          <div class="settlement-row">
            <span class="font-weight-bold">Net Amount</span>
            <strong class="text-primary" style="font-size:1.2rem;">₹{{ formatNumber(calculated.net) }}</strong>
          </div>
        </b-card>

        <!-- Previous Settlements -->
        <b-card title="Previous Settlements">
          <!-- Loading skeleton -->
          <div v-if="loadingHistory">
            <b-skeleton width="40%" class="mb-2"/>
            <b-skeleton-table :rows="5" :columns="4" hide-header/>
          </div>
          <div v-else-if="!history.length" class="text-muted text-center py-3">
            No settlements yet.
          </div>
          <b-table
            v-else
            :items="history"
            :fields="historyFields"
            small
            striped
            responsive
          >
            <template #cell(net_amount)="data">₹{{ formatNumber(data.value) }}</template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BRow, BCol, BButton, BSpinner, BTable,
  BFormGroup, BFormInput,
} from 'bootstrap-vue'

export default {
  name: 'IntegratorSettlement',
  components: {
    BCard, BRow, BCol, BButton, BSpinner, BTable,
    BFormGroup, BFormInput,
  },
  data() {
    return {
      saving: false,
      loadingHistory: false,
      history: [],
      form: {
        birds_sold: 0,
        avg_weight: 0,
        price_per_kg: 0,
        deductions: 0,
        bonus_penalty: 0,
      },
      calculated: {
        gross: 0,
        deductions: 0,
        bonus_penalty: 0,
        net: 0,
      },
      historyFields: [
        { key: 'settlement_date', label: 'Date', formatter: v => v ? v.toString().substring(0, 10) : '—' },
        { key: 'total_birds_sold', label: 'Birds' },
        { key: 'net_amount', label: 'Net Amount' },
      ],
    }
  },
  created() {
    this.fetchHistory()
  },
  methods: {
    calculate() {
      const gross = (parseFloat(this.form.birds_sold) || 0) *
                    (parseFloat(this.form.avg_weight) || 0) *
                    (parseFloat(this.form.price_per_kg) || 0)
      const deductions = parseFloat(this.form.deductions) || 0
      const bonus = parseFloat(this.form.bonus_penalty) || 0
      this.calculated = {
        gross,
        deductions,
        bonus_penalty: bonus,
        net: gross - deductions + bonus,
      }
    },
    async fetchHistory() {
      const { contractId } = this.$route.params
      this.loadingHistory = true
      try {
        const { data } = await axios.get(`web/integrator-contracts/${contractId}/settlements`)
        this.history = data.data || data.items || data || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load settlement history', { variant: 'danger', title: 'Error' })
      } finally {
        this.loadingHistory = false
      }
    },
    async confirmSettlement() {
      const { contractId } = this.$route.params
      this.saving = true
      try {
        const totalWeight = (parseFloat(this.form.birds_sold) || 0) * (parseFloat(this.form.avg_weight) || 0)
        const payload = {
          total_birds_sold: parseInt(this.form.birds_sold) || 0,
          avg_weight_kg: parseFloat(this.form.avg_weight) || 0,
          total_weight_kg: totalWeight,
          price_per_kg: parseFloat(this.form.price_per_kg) || 0,
          other_deductions: parseFloat(this.form.deductions) || 0,
          fcr_bonus: parseFloat(this.form.bonus_penalty) > 0 ? parseFloat(this.form.bonus_penalty) : 0,
          fcr_penalty: parseFloat(this.form.bonus_penalty) < 0 ? Math.abs(parseFloat(this.form.bonus_penalty)) : 0,
          gross_amount: this.calculated.gross,
          net_amount: this.calculated.net,
          settlement_date: new Date().toISOString().substring(0, 10),
        }
        await axios.post(`web/integrator-contracts/${contractId}/settlements`, payload)
        this.$bvToast.toast('Settlement confirmed', { variant: 'success', title: 'Success' })
        this.fetchHistory()
        this.form = { birds_sold: 0, avg_weight: 0, price_per_kg: 0, deductions: 0, bonus_penalty: 0 }
        this.calculate()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to confirm settlement', { variant: 'danger', title: 'Error' })
      } finally {
        this.saving = false
      }
    },
    formatNumber(val) {
      return Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
}
</script>

<style scoped>
.settlement-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
</style>
